import { useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import TableSkeleton from "../../common/TableSkeleton";
import TableError from "../../common/TableError";
import { useGetStudentReport } from "../../../../hooks/school/student/useGetStudentReport";
import "../../../../common/styles/table.css";

/**
 * @typedef {import("../../../../hooks/school/student/useGetStudentReport").StudentReportItem} StudentReportItem
 */

/**
 * StudentTable component for Reports page
 * @param {object} props - Component props
 * @param {Function} [props.onStudentClick] - Callback when a student row is clicked
 * @returns {JSX.Element}
 */
const StudentTable = ({ onStudentClick }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const { loading, error, reportData, getStudentReport, metaData } =
        useGetStudentReport();

    // Fetch student report on mount and page change
    useEffect(() => {
        getStudentReport(currentPage, ITEMS_PER_PAGE);
    }, [currentPage]);

    const handleRefresh = () => {
        getStudentReport(currentPage, ITEMS_PER_PAGE);
    };

    /** @type {StudentReportItem[]} */
    const students = reportData?.students?.data || [];
    const totalPages = metaData?.last_page || 1;


    // Show skeleton only on initial load
    if (loading && reportData === undefined) {
        return <TableSkeleton rows={ITEMS_PER_PAGE} columns={5} />;
    }

    // Show error state
    if (error) {
        return (
            <TableError
                error={error}
                handleRefresh={handleRefresh}
                loading={loading}
            />
        );
    }

    // Show empty state
    if (!students || students.length === 0) {
        return (
            <div className="alert alert-info mt-3" role="alert">
                No students found.
            </div>
        );
    }

    return (
        <div className="custom-table-container mt-3">
            <div className="custom-table-wrapper">
                <Table hover className="custom-table">
                    <thead className="custom-table-head">
                        <tr>
                            <th>Student Name</th>
                            <th>Progress</th>
                            <th>Avg. Score</th>
                            <th>Quests Assigned</th>
                            <th>Lessons Completed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {students.map((student) => (
                            <tr
                                key={student.id}
                                onClick={() =>
                                    onStudentClick && onStudentClick(student)
                                }
                                style={{ cursor: "pointer" }}
                            >
                                <td>{student.name || "-"}</td>
                                <td>{student.progress_percent || 0}%</td>
                                <td>{student.avg_score || 0}</td>
                                <td>{student.quests_assigned || 0}</td>
                                <td>{student.lessons_completed || "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <TablePagination
                totalPage={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                disabled={loading}
            />
        </div>
    );
};

export default StudentTable;
